import React from 'react';
import get from 'lodash/get';
import { GoogleMap, Marker } from '@react-google-maps/api';
import {
  TextField, Grid, InputLabel, IconButton, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import MultiSelect from '../common/multi-select';
import ErrorMessage from '../common/error-message';
import CaseLinkButton from '../common/case-link-button';
import CreatedAndUpdatedDisplay from '../common/created-updated-display';
import PhotosCarousel from '../common/photos-carousel';
import {
  FieldReportStatuses,
  FieldReportResolutionCodes,
} from '../../lib/utils';

const LA_311_CATEGORY = 'LA 311 Report';

const getCaseIcon = () => {
  const svgString = `
    <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
      <circle cx="10" cy="10" r="9" fill="#444" stroke="black" stroke-width="1" opacity="0.8" />
      <text x="10" y="10" text-anchor="middle" fill="white" font-family="Roboto sans-serif" font-size="12" dy=".3em">i</text>
    </svg>
  `;
  return `data:image/svg+xml;base64,${window.btoa(svgString)}`;
};

const getVehicleIcon = () => {
  const svgString = `
    <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
      <circle cx="10" cy="10" r="9" fill="#5bc2e7" stroke="black" stroke-width="1" opacity="0.8" />
    </svg>
  `;
  return `data:image/svg+xml;base64,${window.btoa(svgString)}`;
};

const renderTextField = (value, name) => (
  <TextField
    disabled
    margin="dense"
    name={name}
    type="text"
    variant="standard"
    value={value}
    fullWidth
    multiline
  />
);

const renderCopyButton = (value, label) => (
  <IconButton
    size="small"
    color="inherit"
    aria-label={label}
    onClick={(event) => {
      event.stopPropagation();
      navigator.clipboard.writeText(value);
    }}
    sx={{ ml: 0.25, p: 0.5 }}
  >
    <ContentCopyIcon fontSize="small" />
  </IconButton>
);

class FieldReportEditForm extends React.Component {
  constructor(props) {
    super(props);
    this.selectedType = this.selectedType.bind(this);
    this.onMapLoad = this.onMapLoad.bind(this);
  }

  selectedType(status) {
    const { fieldReport } = this.props;
    const fieldReportStatus = fieldReport.status;
    return fieldReportStatus === status ? 'contained' : 'outlined';
  }

  onMapLoad(map) {
    const { fieldReport } = this.props;
    const caseCoords = get(fieldReport, 'reportedGeo.geoJson.coordinates', []);
    const vehicleCoords = get(fieldReport, 'vehicleGeo.geoJson.coordinates', []);

    if (caseCoords.length && vehicleCoords.length) {
      const mapBounds = new window.google.maps.LatLngBounds();

      mapBounds.extend({ lat: caseCoords[1], lng: caseCoords[0] });
      mapBounds.extend({ lat: vehicleCoords[1], lng: vehicleCoords[0] });

      map.fitBounds(mapBounds);
    }
  }

  render() {
    const {
      handleChange,
      handleLA311ResolutionCode,
      fieldReport,
      errorMessage,
      caseUrls,
      tz,
    } = this.props;

    const resolutionCodeOptions = Object.entries(FieldReportResolutionCodes).map(
      ([value, label]) => ({ value, label, isDisabled: false }),
    );
    resolutionCodeOptions.unshift({ value: '', label: 'None' });

    const isLA311Report = fieldReport.category === LA_311_CATEGORY;
    const caseId = get(fieldReport, 'caseId', '');
    const vehicleId = get(fieldReport, 'vehicle.humanId', '');
    const show311 = get(fieldReport, 'la311.createdBy311', false);
    const photoUrls = get(fieldReport, 'photoUrls', [])
      .concat([get(fieldReport, 'la311.photoUrl', '')])
      .filter(Boolean);

    const getCoords = (name) => {
      const coords = get(fieldReport, `${name}.geoJson.coordinates`, []);
      return coords.length > 1 ? { lat: coords[1], lng: coords[0] } : null;
    };

    const caseCoords = getCoords('reportedGeo');
    const vehicleCoords = getCoords('vehicleGeo');

    return (
      <Grid container spacing={2}>
        {errorMessage && (
          <Grid item xs={12}>
            <ErrorMessage errorMessage={errorMessage} />
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid item xs={12} md={6}>
            <CreatedAndUpdatedDisplay
              item={fieldReport}
              tz={tz}
            />
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={6}>
          <Grid
            container
            item
            xs={12}
            md={6}
            rowSpacing={3}
            columnSpacing={4}
            style={{ height: 'fit-content' }}
          >
            <Grid item xs={12} sm={6} md={5}>
              <InputLabel>
                Vehicle
              </InputLabel>
              <Grid container wrap="nowrap" alignItems="center">
                {renderTextField(vehicleId, 'vehicleId')}
                {vehicleId && renderCopyButton(vehicleId, 'Copy Vehicle Human ID')}
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} md={5}>
              <InputLabel>
                External Ticket ID
              </InputLabel>
              <Grid container wrap="nowrap" alignItems="center">
                {(!caseId || isLA311Report) && renderTextField(caseId, 'caseId')}
                {caseId && !isLA311Report && (
                  <Grid style={{ position: 'relative' }}>
                    {renderTextField('', 'caseId')}
                    <Grid style={{ position: 'absolute', top: '30%' }}>
                      <CaseLinkButton
                        caseUrls={caseUrls}
                        fieldReport={fieldReport}
                        asLink
                      />
                    </Grid>
                  </Grid>
                )}
                {caseId && renderCopyButton(caseId, 'Copy External Ticket ID')}
              </Grid>
            </Grid>
            <Grid item xs={12} md={10}>
              <InputLabel>
                Case text
              </InputLabel>
              <Grid
                sx={{ pr: 1.5 }}
                style={{ minHeight: '9.5em', maxHeight: '9.5em', overflow: 'auto' }}
              >
                {renderTextField(fieldReport.note || '', 'note')}
              </Grid>
            </Grid>
            <Grid item xs={12} md={10}>
              <InputLabel>
                Additional Details
              </InputLabel>
              <TextField
                margin="dense"
                name="comment"
                type="text"
                variant="standard"
                defaultValue={fieldReport.comment || ''}
                fullWidth
                multiline
                onBlur={(event) => handleChange(event, true)}
              />
            </Grid>
            {show311 && (
              <Grid item xs={12} md={10}>
                <InputLabel>
                  LA 311 Resolution Code
                </InputLabel>
                <MultiSelect
                  value={[get(fieldReport, 'la311.resolutionCode', '')]}
                  options={resolutionCodeOptions}
                  isDisabled={fieldReport.status === FieldReportStatuses.CLOSED}
                  isSingle
                  handleChange={(value) => handleLA311ResolutionCode(value)}
                />
              </Grid>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid item xs={12} style={{ minHeight: '16em', maxHeight: '16em' }}>
              {photoUrls.length ? (
                <PhotosCarousel urls={photoUrls} height="16em" />
              ) : (
                <Typography>No photo available</Typography>
              )}
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              {caseCoords || vehicleCoords
                ? (<InputLabel>Approximate Location</InputLabel>)
                : (<Typography>No location available</Typography>)}
              <Grid item xs={12} style={{ position: 'relative', height: '20em', width: '100%' }}>
                {(caseCoords || vehicleCoords) && (
                  <GoogleMap
                    onLoad={this.onMapLoad}
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    zoom={14}
                    center={caseCoords || vehicleCoords}
                    options={{
                      zoomControl: false,
                      mapTypeControl: false,
                      scaleControl: false,
                      streetViewControl: false,
                      rotateControl: false,
                      fullscreenControl: false,
                    }}
                  >
                    {vehicleCoords && (
                      <Marker
                        position={vehicleCoords}
                        icon={{ url: getVehicleIcon() }}
                      />
                    )}
                    {caseCoords && (
                      <Marker
                        position={caseCoords}
                        icon={{ url: getCaseIcon() }}
                      />
                    )}
                  </GoogleMap>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

export default FieldReportEditForm;
